import { SubsystemStatus, DashboardData } from '../../types';
import './Dashboard.css';

// Default subsystem display if no health data
const DEFAULT_SUBSYSTEMS = [
  { subsystem: 'limbic', friendlyName: 'Emotional Core', status: 'unknown', severity: 'info' },
  { subsystem: 'memory', friendlyName: 'Memory Banks', status: 'unknown', severity: 'info' },
  { subsystem: 'explorer', friendlyName: 'Curiosity Engine', status: 'unknown', severity: 'info' },
  { subsystem: 'sandbox', friendlyName: 'Hands (Code)', status: 'unknown', severity: 'info' },
  { subsystem: 'database', friendlyName: 'Long-term Storage', status: 'unknown', severity: 'info' },
  { subsystem: 'daemon', friendlyName: 'Subconscious', status: 'unknown', severity: 'info' },
];

const STATUS_DOTS: Record<string, { className: string; label: string }> = {
  healthy: { className: 'status-dot-healthy', label: 'Online' },
  degraded: { className: 'status-dot-degraded', label: 'Degraded' },
  down: { className: 'status-dot-down', label: 'Down' },
  error: { className: 'status-dot-error', label: 'Error' },
  unknown: { className: 'status-dot-unknown', label: 'Unknown' },
};

function relativeTime(isoString: string): string {
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.round(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

interface SystemStatusProps {
  subsystems: SubsystemStatus[];
  dashboard?: DashboardData;
}

export function SystemStatus({ subsystems, dashboard }: SystemStatusProps) {
  const displayed = subsystems.length > 0 ? subsystems : DEFAULT_SUBSYSTEMS;
  const resources = dashboard?.resources;
  const memoryHealth = dashboard?.memoryHealth;
  const errorHistory = dashboard?.errorHistory;

  // Budget bar calculation
  const budgetPct = resources
    ? Math.min(100, Math.round((resources.tokensToday / resources.dailyBudget) * 100))
    : null;
  const budgetColor = budgetPct !== null
    ? budgetPct > 90 ? '#ef4444' : budgetPct > 70 ? '#f59e0b' : '#4ade80'
    : '#64748b';

  return (
    <div className="panel system-status-panel">
      <h3 className="panel-title">Systems</h3>
      
      {/* Subsystem health dots */}
      <div className="subsystem-grid">
        {displayed.map(sub => {
          const dot = STATUS_DOTS[sub.status] || STATUS_DOTS.unknown;
          return (
            <div key={sub.subsystem} className="subsystem-item">
              <span className={`subsystem-dot ${dot.className}`} title={dot.label} />
              <span className="subsystem-name">{sub.friendlyName}</span>
            </div>
          );
        })}
      </div>

      {/* Resource Usage — Feature 10/13 */}
      {resources && (
        <div className="system-resources">
          <div className="resource-budget-row">
            <span className="resource-label">Budget</span>
            <span className="resource-value" style={{ color: budgetColor }}>{budgetPct}%</span>
          </div>
          <div className="resource-budget-bar">
            <div
              className="resource-budget-fill"
              style={{ width: `${budgetPct}%`, backgroundColor: budgetColor }}
            />
          </div>
          <div className="resource-details">
            <span className="resource-detail">
              {Math.round(resources.tokensToday / 1000)}k / {Math.round(resources.dailyBudget / 1000)}k tokens
            </span>
            <span className="resource-detail">
              ${resources.estimatedCostUSD.toFixed(2)} today
            </span>
          </div>
          {/* Honest pace note, never a countdown: the allowance resets daily (spec §3.1) */}
          {resources.pctOfDailyBudget >= 90 && (
            <div className="resource-warning" title="Share of today's allowance used. Resets at midnight UTC; heavy days self-heal overnight.">
              {resources.pctOfDailyBudget}% of today's allowance used — resets at midnight UTC
            </div>
          )}
        </div>
      )}

      {/* Memory Health — Feature 4B */}
      {memoryHealth && (
        <div className="system-memory-health">
          <div className="memory-health-row">
            <span className="memory-stat">{memoryHealth.totalMemories.toLocaleString()} memories</span>
            <span className="memory-stat">{memoryHealth.recentMemories} this week</span>
          </div>
          {memoryHealth.connectionCount > 0 && (
            <div className="memory-connections">
              🔗 {memoryHealth.connectionCount} connections
            </div>
          )}
        </div>
      )}

      {/* Error History — Feature 10 */}
      {errorHistory && errorHistory.length > 0 && (
        <div className="system-errors">
          <div className="system-errors-header">
            ⚠️ Recent Errors ({errorHistory.length})
          </div>
          <div className="system-errors-list">
            {errorHistory.slice(0, 3).map((err, i) => (
              <div key={i} className={`system-error-item error-${err.severity}`}>
                <span className="error-subsystem">{err.subsystem}</span>
                <span className="error-text">{err.error}</span>
                <span className="error-time">{relativeTime(err.timestamp)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All clear message */}
      {(!errorHistory || errorHistory.length === 0) && resources && (
        <div className="system-all-clear">All systems nominal</div>
      )}
    </div>
  );
}
